/** @jsx jsx */
import { jsx } from '@emotion/react';
import { forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useIsMounted } from '../../../hooks/use-is-mounted';
import getCss from './styles';

export const isTargetEvent = (event) => event.eventPhase === event.AT_TARGET;

export const Backdrop = forwardRef(
  ({ align, onClick, opacity, visible, children, onFadeOut, ...rest }, ref) => {
    const isMounted = useIsMounted();
    const css = getCss({ align, onClick, opacity, visible });
    function handleClick(event) {
      if (onClick && visible && isTargetEvent(event)) onClick();
    }
    function handleKey(event) {
      if (event.key === 'Escape' && visible && onClick) onClick();
    }
    function handleAnimationEnd(event) {
      if (!visible && event.eventPhase === event.AT_TARGET && onFadeOut) onFadeOut();
    }
    const currentRef = ref && ref.current;
    function effect() {
      window.addEventListener('keydown', handleKey, false);
      if (currentRef) {
        currentRef.addEventListener('animationend', handleAnimationEnd, false);
        currentRef.addEventListener('click', handleClick, false);
      }
      return () => {
        window.removeEventListener('keydown', handleKey);
        if (currentRef) {
          currentRef.removeEventListener('animationend', handleAnimationEnd);
          currentRef.removeEventListener('click', handleClick);
        }
      };
    }
    useEffect(effect, [isMounted, visible, currentRef]);
    return (
      <div
        {...rest}
        css={css}
        ref={ref}
        role={onClick ? 'button' : 'dialog'}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={onClick && 0}
      >
        {children}
      </div>
    );
  },
);

Backdrop.propTypes = {
  align: PropTypes.bool,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  onFadeOut: PropTypes.func,
  opacity: PropTypes.number,
  visible: PropTypes.bool.isRequired,
};

Backdrop.defaultProps = {
  align: null,
  opacity: 0.72,
  onClick: null,
  onFadeOut: null,
};
